import { useRef, useState } from 'react';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { useRequiresPlan } from '@/hooks/useRequiresPlan';
import PlanRequiredNotice from '@/components/ui/PlanRequiredNotice';

// The three starter ideas from the Mockup G design
// (project/artifacts/innovate-services-chat-entry-mockup.html). Pills prefill
// the input rather than submitting, so the User can tailor the idea first.
const SUGGESTIONS = [
  'Summarize long documents',
  'Draft replies to reviews',
  'Turn meeting notes into action items',
];

const PLACEHOLDER =
  'e.g. Read incoming support tickets and tag each one by urgency and topic…';

interface ServiceComposeEntryProps {
  /** Called with the described idea when the User submits it. */
  onSubmit: (text: string) => void;
}

/**
 * The "New AI Service" chat-create panel (Mockup G): describe what the service
 * should do in plain words; the composer conversation designs and builds it.
 * Used as the pre-conversation state of ServiceComposePage and as the
 * first-run view of the AI Services list when no services exist yet.
 */
export default function ServiceComposeEntry({ onSubmit }: ServiceComposeEntryProps) {
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Pre-flight no-plan gate (task 467): without a plan the compose build is
  // guaranteed to fail server-side, so the submit is disabled with a reason
  // instead of letting the User author a description into a dead-end.
  const requiresPlan = useRequiresPlan();

  const submit = () => {
    if (!text.trim() || requiresPlan) return;
    onSubmit(text.trim());
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div
      data-testid="service-compose-entry"
      className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-white dark:bg-gray-800 p-5 shadow-sm"
    >
      <h2 className="text-base font-bold text-gray-900 dark:text-white">
        💬 New AI Service
      </h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Describe what it should do — a chat guide designs it with you and builds
        it live.
      </p>

      <div className="mt-4 relative rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
          placeholder={PLACEHOLDER}
          data-testid="service-compose-input"
          className="block w-full resize-none rounded-lg border-0 bg-transparent px-3 py-2.5 pr-12 text-sm text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:ring-0"
        />
        <button
          type="button"
          onClick={submit}
          disabled={!text.trim() || requiresPlan}
          data-testid="service-compose-submit"
          aria-label="Start composing"
          className="absolute bottom-2 right-2 inline-flex items-center justify-center rounded-md bg-indigo-600 p-2 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PaperAirplaneIcon className="h-4 w-4" />
        </button>
      </div>

      {requiresPlan && <PlanRequiredNotice className="mt-2" />}

      <div className="mt-3 flex flex-wrap gap-2">
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => {
              setText(s);
              textareaRef.current?.focus();
            }}
            className="rounded-full border border-indigo-200 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/20 px-3 py-1.5 text-xs font-medium text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}
